import { useEffect, useState } from "react";
import { useLocation, Outlet } from "react-router-dom";
import { Box, Flex, Button, IconButton, ScrollArea } from "@radix-ui/themes";
import { Menu, Minimize, Maximize, BotMessageSquare } from "lucide-react";
import { AdminNavigation } from "@/components/navigation/AdminNavigation";
import { GlobalSearch } from "@/components/ui/GlobalSearch";
import { ProfileHeader } from "@/components/ui/ProfileHeader";
import { Breadcrumb } from "@/components/ui/Breadcrumb";
import { AISidebarPanel } from "@/components/ui/AiSidebarPanel";
import { TenantThemeProvider } from "@/providers/TenantThemeProvider";
import { useFullscreen } from "@/hooks/useFullscreen";
import { ThemeToggle } from "@/components/ui/ThemeToggle";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";

export default function AuthenticatedLayout() {
  const location = useLocation();
  const { isFullscreen, exitFullscreen, toggleFullscreen } = useFullscreen();
  const [showSidebar, setShowSidebar] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(() => {
    const stored = localStorage.getItem("anythink_sidebar_open");
    return stored !== null ? stored === "true" : true; // Default open
  });
  const [chatWidth, setChatWidth] = useState(() => {
    const stored = localStorage.getItem("anythink_sidebar_width");
    return stored ? parseInt(stored, 10) : 420;
  });
  const isAIChatEnabled = useFeatureFlag("AnythinkAIChat");

  // Persist sidebar state
  const toggleChat = (open: boolean) => {
    setIsChatOpen(open);
    localStorage.setItem("anythink_sidebar_open", String(open));
  };
  const handleWidthChange = (w: number) => {
    setChatWidth(w);
    localStorage.setItem("anythink_sidebar_width", String(w));
  };

  useEffect(() => {
    // Report navigation to parent if in iframe
    if (window.parent !== window) {
      window.parent.postMessage(
        {
          type: "navigation",
          url: window.location.href,
        },
        "*",
      );
    }
  }, [location]);

  // Intercept new-tab/external link clicks in iframe to open them securely via parent transfer token flow
  useEffect(() => {
    if (window.parent === window) return;

    const handleGlobalClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a");
      if (anchor) {
        const isNewTabClick =
          e.metaKey ||
          e.ctrlKey ||
          e.button === 1 ||
          anchor.getAttribute("target") === "_blank";

        if (isNewTabClick) {
          const href = anchor.getAttribute("href");
          if (href && href !== "#") {
            e.preventDefault();
            e.stopPropagation();
            window.parent.postMessage(
              {
                type: "open-in-new-tab",
                path: href,
              },
              "*",
            );
          }
        }
      }
    };

    window.addEventListener("click", handleGlobalClick, true);
    window.addEventListener("auxclick", handleGlobalClick, true);
    return () => {
      window.removeEventListener("click", handleGlobalClick, true);
      window.removeEventListener("auxclick", handleGlobalClick, true);
    };
  }, []);

  const isWorkflowPage = /^\/org\/[^/]+\/workflows\/[^/]+$/.test(
    location.pathname,
  );

  const pathMatch = location.pathname.match(/\/entities\/([^/]+)/);
  const currentEntityName = pathMatch ? pathMatch[1] : "page";

  return (
    <TenantThemeProvider>
      <Flex style={{ minHeight: "100vh", height: "100vh", overflow: "hidden" }}>
        {!isFullscreen && (
          <Box
            style={{
              borderRight: "1px solid var(--gray-5)",
              transition: "all 300ms",
              backgroundColor: "var(--sidebar-bg, var(--gray-2))",
              flexShrink: 0,
            }}
            asChild
          >
            <aside>
              <AdminNavigation />
            </aside>
          </Box>
        )}

        {/* Floating sidebar for fullscreen mode */}
        {isFullscreen && (
          <>
            <Flex
              gap="2"
              style={{
                position: "fixed",
                top: "1rem",
                right: isChatOpen ? `${chatWidth + 16}px` : "1rem",
                zIndex: 1000,
                transition: "right 200ms ease",
              }}
            >
              <Button
                variant="solid"
                size="1"
                onClick={() => setShowSidebar(!showSidebar)}
                title="Toggle Sidebar"
              >
                <Menu className="w-4 h-4" />
              </Button>
              <Button
                variant="solid"
                size="1"
                onClick={exitFullscreen}
                title="Exit Fullscreen"
              >
                <Minimize className="w-4 h-4" />
              </Button>
            </Flex>

            {showSidebar && (
              <Box
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  height: "100vh",
                  borderRight: "1px solid var(--gray-5)",
                  transition: "all 300ms",
                  backgroundColor: "var(--sidebar-bg, var(--gray-2))",
                  zIndex: 999,
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                }}
                asChild
              >
                <aside>
                  <AdminNavigation />
                </aside>
              </Box>
            )}

            {showSidebar && (
              <Box
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(0, 0, 0, 0.1)",
                  zIndex: 998,
                }}
                onClick={() => setShowSidebar(false)}
              />
            )}
          </>
        )}

        {/* Main content area — shrinks when AI panel is open */}
        <Flex
          direction="column"
          style={{ flex: 1, minWidth: 0, overflow: "hidden" }}
        >
          {!isFullscreen && (
            <Box
              style={{
                height: "4rem",
                borderBottom: "1px solid var(--gray-5)",
                backgroundColor: "var(--sidebar-bg, var(--gray-1))",
                flexShrink: 0,
              }}
              asChild
            >
              <header>
                <Flex
                  justify="between"
                  align="center"
                  px="6"
                  style={{ height: "100%" }}
                >
                  <GlobalSearch />
                  <Flex gap="4" align="center">
                    <Button
                      variant="ghost"
                      size="1"
                      onClick={() => toggleFullscreen(currentEntityName)}
                      title={
                        isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"
                      }
                    >
                      {isFullscreen ? (
                        <Minimize className="w-4 h-4" />
                      ) : (
                        <Maximize className="w-4 h-4" />
                      )}
                    </Button>

                    {isAIChatEnabled && (
                      <IconButton
                        variant={isChatOpen ? "solid" : "ghost"}
                        onClick={() => toggleChat(!isChatOpen)}
                        title={isChatOpen ? "Close AI panel" : "Open AI panel"}
                        style={{
                          padding: "var(--space-2)",
                        }}
                      >
                        <BotMessageSquare size={20} />
                      </IconButton>
                    )}
                    <ThemeToggle />
                    <ProfileHeader />
                  </Flex>
                </Flex>
              </header>
            </Box>
          )}
          <ScrollArea
            className="grid-bg"
            style={{
              flex: 1,
              backgroundColor: "var(--content-bg)",
            }}
            asChild
          >
            <main>
              <Box
                style={{
                  padding: isWorkflowPage
                    ? 0
                    : isFullscreen
                      ? "0.5rem"
                      : "2rem",
                  minHeight: "100%",
                }}
              >
                <Box style={{ width: "100%" }}>
                  {!isWorkflowPage && !isFullscreen && (
                    <Breadcrumb className="mb-6" />
                  )}
                  <Outlet />
                </Box>
              </Box>
            </main>
          </ScrollArea>
        </Flex>

        {/* AI Sidebar Panel — sits alongside content, no overlay */}
        {isAIChatEnabled && isChatOpen && (
          <AISidebarPanel
            onClose={() => toggleChat(false)}
            width={chatWidth}
            onWidthChange={handleWidthChange}
          />
        )}
      </Flex>
    </TenantThemeProvider>
  );
}
